import { Geolocation } from '@capacitor/geolocation';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonGrid,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonRouterLink,
  IonRow,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonText,
  IonToolbar,
} from '@ionic/react';
import { close, ellipsisHorizontal, informationCircleOutline, locate, search } from 'ionicons/icons';
import * as React from 'react';
import { useMap } from 'react-map-gl/maplibre';

import { css } from '@emotion/react';
import { ReactFitty } from 'react-fitty';
import { fetchCurrentLocation } from '../../api/services/current-location';
import { useReverseLookupLocation } from '../../api/services/reverse-lookup-location';
import { useSearchLocation } from '../../api/services/search-location';
import CircleUrl from '../../assets/img/circle.svg';
import LocateOnIconUrl from '../../assets/img/locate_on_icon.svg';
import MapPinUrl from '../../assets/img/map_pin.svg';
import SquareUrl from '../../assets/img/square.svg';
import { AqiStandardProperties } from '../../common/analytics/analytics-properties';
import { AnalyticsEventName } from '../../common/analytics/types';
import { trackEvent } from '../../common/analytics/utils';
import { WLOMException } from '../../common/exceptions';
import { LocationSearchResult } from '../../common/location-search-result';
import { MapData } from '../../common/map-data';
import { useSettings } from '../../contexts/SettingsContext';
import * as commonCss from '../../styles/common';
import { SourceType, type Point } from '../../types';
import { AqiInfo } from '../AqiInfo';
import { SkeletonText } from '../SkeletonText';
import { Spinner } from '../Spinner';
import { Title } from '../Title';

interface MapCardProps {
  onError: (error: WLOMException) => void;
  onGeoLocate?: (location: Point) => void;
  onSelectSearchResult?: (result: LocationSearchResult) => void;
  onClearSearch?: () => void;
  mapData: MapData[];
}

const MapCard: React.FC<MapCardProps> = ({ onGeoLocate, onSelectSearchResult, onClearSearch, onError, mapData }) => {
  const { aqMap: map } = useMap();
  const {
    aqiStandard,
    availableAqiStandards,
    network,
    mapSettings,
    pollutantConfigs,
    myLocation,
    searchedLocation,
    viewport,
    isLocationPermissionGranted,
    setMyLocation,
    setAqiStandardId,
    setLocationPermissionGranted,
  } = useSettings();
  const { reverseLookupLocation } = useReverseLookupLocation();

  const searchBarRef = React.useRef<HTMLIonSearchbarElement>(null);
  const [searchResults, setSearchResults] = React.useState<LocationSearchResult[]>([]);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [isSearchLoading, setIsSearchLoading] = React.useState<boolean>(false);
  const [isLoadingLocation, setIsLoadingLocation] = React.useState<boolean>(false);
  const [isSearchQueryInitialized, setIsSearchQueryInitialized] = React.useState<boolean>(false);
  const { searchLocation, updateSearchCache } = useSearchLocation();

  const checkLocationPermission = async (): Promise<boolean> => {
    const permissions = await Geolocation.checkPermissions();
    console.log('permissions', permissions);
    if (permissions.location !== 'denied') {
      setLocationPermissionGranted(true);
      return true;
    }
    setLocationPermissionGranted(false);
    return false;
  };

  // Keep the search cache up to date
  React.useEffect(() => {
    updateSearchCache(mapData);
  }, [updateSearchCache, mapData]);

  // Initialize search query if empty and there's a searched location available
  React.useEffect(() => {
    if (!isSearchQueryInitialized && !searchQuery && searchedLocation) {
      setIsSearchQueryInitialized(true);
      reverseLookupLocation(searchedLocation).then((result) => {
        const displayName = result?.displayName ?? '';
        setSearchQuery(displayName);
      });
    }
  }, [searchedLocation, searchQuery, isSearchQueryInitialized, reverseLookupLocation]);

  // Keep search bar ref up to date with the search query
  React.useEffect(() => {
    if (searchBarRef.current) {
      searchBarRef.current.value = searchQuery;
    }
  }, [searchBarRef, searchQuery]);

  const toggleGeoLocate = React.useCallback(async () => {
    setIsLoadingLocation(true);
    try {
      let currentLocation: Point | null = null;
      try {
        currentLocation = await fetchCurrentLocation(mapSettings?.bbox ?? undefined);
        setMyLocation(currentLocation);
      } catch (e) {
        if (e instanceof WLOMException) {
          onError(e);
        }
      }
      await checkLocationPermission();
      if (!currentLocation) {
        return;
      }
      onGeoLocate?.(currentLocation);
    } finally {
      setIsLoadingLocation(false);
    }
  }, [checkLocationPermission, onGeoLocate, onError, mapSettings?.bbox]);

  const onClickGeoLocate = React.useCallback(async () => {
    if (myLocation) {
      setMyLocation(null);
    } else {
      toggleGeoLocate();
    }
  }, [setMyLocation, myLocation, toggleGeoLocate]);

  const onClickSearchResult = React.useCallback(
    (result: LocationSearchResult) => {
      setSearchResults([]);
      setSearchQuery(result.label);
      onSelectSearchResult?.(result);
    },
    [onSelectSearchResult]
  );

  const handleSearchInput = React.useCallback(
    async (e: Event) => {
      const target = e.target as HTMLIonSearchbarElement;
      if (!target) {
        return;
      }
      setIsSearchLoading(true);
      try {
        const query = target.value!;
        setSearchQuery(query);
        if (query.length > 2) {
          const suggestions = await searchLocation(query, viewport, mapData, mapSettings?.bbox ?? undefined);
          setSearchResults(suggestions);
        } else {
          setSearchResults([]);
        }
      } finally {
        setIsSearchLoading(false);
      }
    },
    [mapSettings?.bbox, mapData, searchLocation, viewport]
  );

  const handleClearSearch = React.useCallback(() => {
    setSearchResults([]);
    setSearchQuery('');
    onClearSearch?.();
  }, [onClearSearch]);

  const handleAqiInfoOpened = React.useCallback(() => {
    if (!aqiStandard) {
      return;
    }
    const eventProperties: AqiStandardProperties = {
      aqiStandardId: aqiStandard.aqiStandardId,
      name: aqiStandard.name,
      aqiClassType: aqiStandard.aqiClassType,
      pollutantConfigs: aqiStandard.pollutantConfigs.map((config) => ({
        pollutant: config.pollutant,
        displayName: config.displayName,
      })),
    };
    trackEvent(AnalyticsEventName.OpenedAqiInfoPanel, eventProperties);
  }, [aqiStandard]);

  const aqiFontSize = `min(${commonCss.SMALL_FONT_SIZE_REM}rem, 3.2vw)`;
  const mapCardCss = {
    card: [
      css({
        zIndex: 4,
        width: 400,
        margin: 0,
        [commonCss.MediaQueries.sm]: {
          width: '100%',
        },
      }),
    ],
    cardHeader: [commonCss.noPadding],
    cardContent: [
      css({
        padding: 8,
      }),
    ],
    locationSuggestionList: [
      css({
        maxHeight: '50vh',
        overflowY: 'auto',
      }),
    ],
    aqiHeader: [
      css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--ion-color-secondary)',
        fontWeight: 'bold',
      }),
    ],
    aqiInfoButton: [
      css({
        padding: 0,
        margin: 0,
      }),
    ],
    popover: [
      css({
        '--width': '400px',
      }),
    ],
    aqiInfoIcon: [
      css({
        color: 'var(--ion-color-secondary)',
      }),
    ],
    aqiHorizontalBar: [
      css({
        display: 'flex',
        width: '100%',
        height: 5,
        borderRadius: 2,
      }),
    ],
    aqiDescriptions: [
      commonCss.textAlign.end,
      css({ backgroundColor: 'var(--background-color)', flex: 1, fontSize: aqiFontSize }),
    ],
    aqiBreakpoints: [
      commonCss.noPadding,
      commonCss.textAlign.end,
      css({ flex: 1, width: '100%', fontSize: aqiFontSize }),
    ],
    pollutantSelector: [css({ width: '5rem' })],
    geolocateButton: [
      css({
        width: '2.55rem',
        height: '2.55rem',
        '.md &': {
          boxShadow:
            'rgba(0, 0, 0, 0.2) 0px 2px 2px 0px, rgba(0, 0, 0, 0.14) 0px 3px 1px -2px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px',
        },
        '.ios &': {
          width: 'calc(1.0625rem + 12px)',
          height: 'calc(1.5625rem + 12px)',
        },
      }),
    ],
    toolbar: [
      css({
        borderBottom: '0.55px solid',
        borderColor: 'var(--border-color)',
        '--background': 'transparent',
      }),
    ],
    searchbar: [
      css({
        position: 'relative',
        '--background': '#fff',
        ['.searchbar-input-container.sc-ion-searchbar-ios']: {
          border: '1px solid',
          borderColor: 'var(--ion-background-color-step-150)',
          borderRadius: 4,
        },
        [commonCss.MediaQueries.sm]: {
          ['.ios &']: {
            padding: '0 8px 0 0',
          },
        },
      }),
    ],
    clearButton: css({
      fontSize: '1.5rem',
      position: 'absolute',
      right: '4rem',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1,
      cursor: 'pointer',
    }),
    searchResultOutOfBounds: [
      css({
        color: 'var(--ion-color-grey)',
        cursor: 'not-allowed',
      }),
    ],
    searchResultIcon: [
      css({
        marginRight: '1rem',
      }),
    ],
    topRow: [css({ display: 'flex', padding: '0.5rem' })],
    logoImg: [css({ display: 'flex', flex: 1, height: 70, marginRight: '1rem' })],
    title: [css({ display: 'flex', flex: 2, alignItems: 'center' })],

    aqiSelectorContainer: [
      css({
        display: 'flex',
        justifyContent: 'center',
        // manual adjustment to keep the select box at the same height as the text
        height: '27px',
        marginTop: '-18px',
      }),
    ],
    aqiSelector: [
      css({
        '--highlight-color-focused': 'transparent',
      }),
    ],
  };

  const networkHeader = network ? (
    <div css={mapCardCss.topRow}>
      <IonImg src={network.logoUrl} alt={network.name} css={mapCardCss.logoImg} />
      <div css={mapCardCss.title}>
        <ReactFitty maxSize={22}>
          <Title fontSize="inherit">{network.name}</Title>
        </ReactFitty>
      </div>
    </div>
  ) : null;

  return (
    <>
      <IonCard css={mapCardCss.card} id="map-card-content">
        <IonCardHeader css={mapCardCss.cardHeader}>
          <IonToolbar css={mapCardCss.toolbar}>
            {network ? (
              network.networkUrl ? (
                <IonRouterLink href={network.networkUrl} target="_blank" rel="noopener noreferrer">
                  {networkHeader}
                </IonRouterLink>
              ) : (
                networkHeader
              )
            ) : (
              <SkeletonText height={86} />
            )}
          </IonToolbar>
        </IonCardHeader>
        <IonCardContent css={mapCardCss.cardContent}>
          <IonGrid>
            <IonRow>
              <IonCol>
                <IonRow css={commonCss.smallText}>
                  {availableAqiStandards && aqiStandard && pollutantConfigs && (
                    <IonCol css={[commonCss.noPadding, mapCardCss.aqiHeader]}>
                      {availableAqiStandards.length < 2 ? (
                        <IonText>{aqiStandard.name}</IonText>
                      ) : (
                        <div css={mapCardCss.aqiSelectorContainer}>
                          <IonSelect
                            value={aqiStandard.aqiStandardId}
                            aria-label="Aqi Standard Selector"
                            interface="popover"
                            css={mapCardCss.aqiSelector}
                            onIonChange={(e) => {
                              const newAqiStandardId = e.detail.value;
                              setAqiStandardId(newAqiStandardId);
                            }}
                          >
                            {availableAqiStandards.map((standard) => (
                              <IonSelectOption key={standard.aqiStandardId} value={standard.aqiStandardId}>
                                {standard.name}
                              </IonSelectOption>
                            ))}
                          </IonSelect>
                        </div>
                      )}
                      <IonButton fill="clear" size="small" id="btn-aqi-info" css={mapCardCss.aqiInfoButton}>
                        <IonIcon css={mapCardCss.aqiInfoIcon} icon={informationCircleOutline} />
                      </IonButton>
                      <IonPopover
                        trigger="btn-aqi-info"
                        css={mapCardCss.popover}
                        onIonPopoverDidPresent={handleAqiInfoOpened}
                      >
                        <AqiInfo pollutantConfigs={pollutantConfigs} aqiStandard={aqiStandard}></AqiInfo>
                      </IonPopover>
                    </IonCol>
                  )}
                </IonRow>
                <IonRow>
                  {aqiStandard ? (
                    <div css={mapCardCss.aqiHorizontalBar}>
                      {aqiStandard.aqiLevels.map(({ aqiLevelId, color }) => {
                        // Note: here we use the `style` attribute to set CSS custom properties
                        // for performance reasons as explained in the documentation here:
                        // https://emotion.sh/docs/best-practices#advanced-css-variables-with-style
                        return (
                          <div
                            key={aqiLevelId}
                            css={mapCardCss.aqiDescriptions}
                            style={{ '--background-color': color } as React.CSSProperties}
                          ></div>
                        );
                      })}
                    </div>
                  ) : (
                    <SkeletonText height={20} />
                  )}
                </IonRow>
                <IonRow css={commonCss.smallText}>
                  {aqiStandard && (
                    <>
                      <IonCol css={commonCss.noPadding}>
                        <IonText>{aqiStandard.aqiLevels[0].description}</IonText>
                      </IonCol>
                      <IonCol css={[commonCss.noPadding, commonCss.textAlign.end]}>
                        <IonText>{aqiStandard.aqiLevels[aqiStandard.aqiLevels.length - 1].description}</IonText>
                      </IonCol>
                    </>
                  )}
                </IonRow>
              </IonCol>
            </IonRow>
            {map ? (
              <IonButtons>
                <IonSearchbar
                  ref={searchBarRef}
                  placeholder="Search place"
                  onIonInput={handleSearchInput}
                  debounce={500}
                  showClearButton="never"
                  css={mapCardCss.searchbar}
                  className="tour-searchbar"
                  searchIcon={isSearchLoading ? ellipsisHorizontal : search}
                />
                {(searchQuery.length > 0 || searchResults.length > 0 || searchedLocation) && (
                  <IonIcon icon={close} css={mapCardCss.clearButton} onClick={handleClearSearch} />
                )}
                <IonButton
                  disabled={isLocationPermissionGranted === false}
                  onClick={onClickGeoLocate}
                  css={mapCardCss.geolocateButton}
                  fill="solid"
                  color="primary"
                  className="tour-geolocate-button"
                >
                  {!isLoadingLocation ? (
                    myLocation ? (
                      <IonIcon src={LocateOnIconUrl} slot="icon-only" />
                    ) : (
                      <IonIcon icon={locate} slot="icon-only" />
                    )
                  ) : (
                    <Spinner type="dots" slot="icon-only" />
                  )}
                </IonButton>
              </IonButtons>
            ) : (
              <IonRow>
                <IonCol>
                  <SkeletonText height={40} />
                </IonCol>
                <IonCol size="2">
                  <SkeletonText height={40} />
                </IonCol>
              </IonRow>
            )}
            {aqiStandard && pollutantConfigs && searchResults.length > 0 && (
              <IonRow>
                <IonCol size="12">
                  <IonList css={mapCardCss.locationSuggestionList}>
                    {searchResults.map((result, index) =>
                      result.isOutOfBounds ? (
                        <IonItem
                          css={[commonCss.smallText, mapCardCss.searchResultOutOfBounds]}
                          key={index}
                          button={false}
                        >
                          <IonIcon src={MapPinUrl} css={mapCardCss.searchResultIcon}></IonIcon>
                          <IonLabel>(Outside of network) {result.label}</IonLabel>
                        </IonItem>
                      ) : (
                        <IonItem
                          css={commonCss.smallText}
                          key={index}
                          button
                          onClick={() => onClickSearchResult(result)}
                        >
                          {result.isDatasourceSearchResult ? (
                            <IonIcon
                              icon={result.mapData!.sourceType === SourceType.CLARITY_NODE ? CircleUrl : SquareUrl}
                              css={mapCardCss.searchResultIcon.concat([
                                css({ color: result.mapData!.getColor(aqiStandard, pollutantConfigs) }),
                              ])}
                            />
                          ) : (
                            <IonIcon css={mapCardCss.searchResultIcon} src={MapPinUrl}></IonIcon>
                          )}
                          <IonLabel css={result.mapData ? commonCss.bold : {}}>{result.label}</IonLabel>
                        </IonItem>
                      )
                    )}
                  </IonList>
                </IonCol>
              </IonRow>
            )}
          </IonGrid>
        </IonCardContent>
      </IonCard>
    </>
  );
};

export default MapCard;
